import * as Sentry from '@sentry/react';
import { CaptureConsole } from '@sentry/integrations';
import { createLogger } from './logger';

const logger = createLogger('Sentry');

/**
 * Initialize Sentry error tracking
 * @param {Object} options - Configuration options
 * @param {string} options.dsn - Sentry DSN
 * @param {string} options.environment - Current environment
 * @param {boolean} options.debug - Enable debug mode
 */
export const initSentry = (options = {}) => {
  const {
    dsn = process.env.REACT_APP_SENTRY_DSN,
    environment = process.env.REACT_APP_ENVIRONMENT || 'development',
    debug = process.env.NODE_ENV === 'development',
  } = options;

  if (!dsn) {
    // Sentry is intentionally optional. No need to warn on every render or
    // HMR cycle — this is an expected dev/config state, not an error.
    // To re-enable, set REACT_APP_SENTRY_DSN in your .env.
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment,
      debug,
      integrations: [
        // Capture console errors automatically
        new CaptureConsole({
          levels: ['error', 'warn'],
        }),
        // React error boundary integration
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: true,
          useLocation: true,
          useNavigationType: true,
        }),
        // Performance monitoring
        Sentry.browserTracingIntegration(),
      ],
      // Performance monitoring sample rate
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
      // Session replay sample rate
      replaysSessionSampleRate: environment === 'production' ? 0.1 : 0,
      replaysOnErrorSampleRate: 1.0,
      // Before sending errors
      beforeSend(event, hint) {
        // Filter out specific errors
        if (event.exception) {
          const error = hint.originalException;

          // Don't send network errors in development
          if (environment === 'development' && error?.name === 'NetworkError') {
            return null;
          }

          // Don't send canceled requests
          if (error?.message?.includes('canceled')) {
            return null;
          }
        }

        // Remove sensitive data
        if (event.request) {
          delete event.request.cookies;
          delete event.request.headers;
        }

        return event;
      },
      // Ignore specific errors
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
        // Network errors
        /Network request failed/,
        /Failed to fetch/,
        // Firebase auth errors that are handled
        /auth\/(popup-blocked|network-request-failed)/,
      ],
    });

    logger.info('Sentry initialized successfully');
  } catch (error) {
    logger.error('Failed to initialize Sentry:', error);
  }
};

/**
 * Set user context for error tracking
 * @param {Object} user - User information
 */
export const setSentryUser = user => {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.uid,
    email: user.email,
    username: user.displayName,
  });
};

/**
 * Add breadcrumb for tracking user actions
 * @param {Object} breadcrumb - Breadcrumb data
 */
export const addBreadcrumb = breadcrumb => {
  Sentry.addBreadcrumb({
    timestamp: Date.now(),
    ...breadcrumb,
  });
};

/**
 * Capture custom error
 * @param {Error} error - Error to capture
 * @param {Object} context - Additional context
 */
export const captureError = (error, context = {}) => {
  Sentry.captureException(error, {
    contexts: {
      custom: context,
    },
  });
};

/**
 * Capture message
 * @param {string} message - Message to capture
 * @param {string} level - Severity level
 */
export const captureMessage = (message, level = 'info') => {
  Sentry.captureMessage(message, level);
};

/**
 * Profile a transaction
 * @param {string} name - Transaction name
 * @param {Function} callback - Function to profile
 */
export const profileTransaction = async (name, callback) => {
  const transaction = Sentry.startSpan({ name }, async () => {
    try {
      const result = await callback();
      return result;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  });

  return transaction;
};

// Export Sentry instance for advanced usage
export { Sentry };
